import { useFormikContext } from 'formik';
import { ChangeEvent, ReactElement } from 'react';
import { object, reach } from 'yup';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { COURSE_CREDIT_FIELDS } from '../../../types/NeglectedOrDelinquentHighSchoolCourseCreditsFields';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormRadioList from '../../FormInputs/FormRadioList/FormRadioList';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const question =
    'Did the LEA award high school course credit(s) to students being served in the ' +
    'program during the selected school year?';
const yesNoOptions = [
    { value: 'Yes', text: 'Yes' },
    { value: 'No', text: 'No' },
];

const NeglectedOrDelinquentHighSchoolCourseCreditsPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors, setFieldValue, setFieldTouched } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    const credits = values.highSchoolCourseCredits;
    const showCounts = credits.awardedCourseCredits === 'Yes';
    const awardedField = 'highSchoolCourseCredits.awardedCourseCredits';
    const radioSchema = object().shape({
        [awardedField]: reach(validationSchema, awardedField),
    });
    const canCalculate = COURSE_CREDIT_FIELDS.every(({ key }) =>
        /^\d{1,6}$/.test(credits[key]));
    const total = canCalculate
        ? COURSE_CREDIT_FIELDS.reduce((sum, { key }) => sum + Number(credits[key]), 0)
        : '';

    const handleAwardedClick = (event: ChangeEvent<HTMLButtonElement>): void => {
        if (event.target.value === 'No') {
            COURSE_CREDIT_FIELDS.forEach(({ key }) => {
                const field = `highSchoolCourseCredits.${key}`;
                setFieldValue(field, '', false);
                setFieldTouched(field, false, false);
            });
        }
    };

    return (
        <Panel panelType={PanelType.INFO} heading="High School Course Credits">
            <p>
                Provide information about students who earned high school course credits
                during or after participation in the program.
            </p>
            <FormRadioList
                field={awardedField}
                displayName={question}
                options={yesNoOptions}
                optionOrientation={FormRadioListOrientation.Horizontal}
                disabled={disabled}
                validationSchema={radioSchema}
                onClick={handleAwardedClick}
            />
            {showCounts && (
                <>
                    <p>
                        Provide the <strong>number of students</strong> who earned high
                        school course credits:
                    </p>
                    <div className="nord-course-credits">
                        {COURSE_CREDIT_FIELDS.map(({ key, label }, index) => {
                            const field = `highSchoolCourseCredits.${key}`;
                            const inputSchema = object().shape({
                                [field]: reach(validationSchema, field).resolve({
                                    parent: credits,
                                    value: credits[key],
                                }),
                            });
                            const warning = index === 0
                                ? errors.courseCreditsWhileLimit
                                : errors.courseCreditsExitLimit;
                            return (
                                <div key={key}>
                                    <FormTextbox
                                        field={field}
                                        displayName={label}
                                        disabled={disabled}
                                        validationSchema={inputSchema}
                                    />
                                    {typeof warning === 'string' && (
                                        <p className="nord-demographics-error"
                                            role="alert">
                                            {warning}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                        <div className="field-container disabled">
                            <FormInputLabel
                                field="totalStudentsEarnedCourseCredits"
                                displayName="Total Students who Earned Course Credits"
                                disabled
                            />
                            <input
                                id="totalStudentsEarnedCourseCredits"
                                className="data-input"
                                type="text"
                                disabled
                                readOnly
                                value={total}
                            />
                        </div>
                    </div>
                </>
            )}
        </Panel>
    );
};

export default NeglectedOrDelinquentHighSchoolCourseCreditsPanel;
